// Folding grouped generations into what the grid actually draws.
//
// The list endpoint returns flat rows plus a groups map; the Output grid draws
// one card per GROUP, not per row. Keeping that fold here — pure, no React —
// means the ordering and status rules are testable on their own, and the card
// component stays a renderer.

import { FinalizeItem, GenerationRow, GroupInfo } from './types';

/** One card in the grid: a loose generation, or a folded stack of siblings. */
export type DisplayUnit =
  | { kind: 'single'; row: GenerationRow }
  | { kind: 'group'; groupId: string; rows: GenerationRow[]; info: GroupInfo };

/**
 * Fold rows into cards, preserving encounter order: a group takes the slot of
 * its FIRST member and later members collect onto it, so the grid keeps the
 * server's ordering instead of hoisting groups to the top.
 *
 * A row whose groupId has no entry in the map (a group filtered away, or a
 * stale id) renders loose rather than vanishing.
 *
 * The 1-member guard: a group with one visible row AND one member server-side
 * has nothing to stack or share copy across, so it draws as a plain card. Note
 * the AND — one visible row out of many members is a filtered group, and still
 * draws folded (with the "1 of N shown" caption).
 */
export function foldRows(rows: GenerationRow[], groups: Record<string, GroupInfo>): DisplayUnit[] {
  const units: DisplayUnit[] = [];
  const slotOf = new Map<string, number>();

  for (const row of rows) {
    const groupId = row.groupId;
    const info = groupId ? groups[groupId] : undefined;
    if (!groupId || !info) {
      units.push({ kind: 'single', row });
      continue;
    }
    const slot = slotOf.get(groupId);
    if (slot === undefined) {
      slotOf.set(groupId, units.length);
      units.push({ kind: 'group', groupId, rows: [row], info });
    } else {
      (units[slot] as { rows: GenerationRow[] }).rows.push(row);
    }
  }

  return units.map(unit => (
    unit.kind === 'group' && unit.rows.length === 1 && unit.info.memberCount <= 1
      ? { kind: 'single', row: unit.rows[0] }
      : unit
  ));
}

/** Size in Marsha's file-name style: 9:16 → 9X16, 1:1 → 1X1. */
export function sizeLabel(row: GenerationRow): string {
  return (row.aspectRatio || '').replace(/:/g, 'X') || 'ad';
}

/** A publish name: product, day, size — numbered only when it repeats. */
export function defaultName(row: GenerationRow, n: number): string {
  const day = new Date().toISOString().slice(0, 10);
  const base = `${row.productName || 'Static'} — ${day} — ${sizeLabel(row)}`;
  return n > 1 ? `${base} #${n}` : base;
}

const dedupeKey = (row: GenerationRow) => `${row.productName || '?'}|${sizeLabel(row)}`;

/**
 * Default names for a whole selection, keyed by generationId and — for each
 * stack — by groupId.
 *
 * ONE counter namespace per product+size(+day) covers stacks AND loose rows.
 * Counting them separately handed a loose row and a stack of the same product
 * and size the IDENTICAL default name, and Notion dedupes on the exact title:
 * the second one published its files and got no row at all.
 *
 * A stack takes the number of its FIRST member rather than one of its own,
 * because only ever one of the two is sent — 'batch' publishes the stack name,
 * 'individual' publishes the member names. That keeps the numbers tight (a
 * loose row next to a stack reads "#2", not "#4") while every name that can
 * ship together stays distinct.
 *
 * A selection with no stacks is numbered exactly as it always was.
 */
export function buildDefaultNames(rows: GenerationRow[]): Record<string, string> {
  const counters: Record<string, number> = {};
  const named: Record<string, string> = {};
  const seenGroups = new Set<string>();

  for (const row of rows) {
    const key = dedupeKey(row);
    counters[key] = (counters[key] || 0) + 1;
    named[row.generationId] = defaultName(row, counters[key]);

    const groupId = row.groupId;
    if (groupId && !seenGroups.has(groupId)) {
      seenGroups.add(groupId);
      named[groupId] = defaultName(row, counters[key]);
    }
  }

  return named;
}

/**
 * What the finalize dialog PREFILLS: a creative's own stored name when it has
 * one, else the derived default.
 *
 * `buildDefaultNames` is left exactly as it is — its numbering contract is
 * pinned by tests and a stored name has no business inside a counter that
 * exists to keep DERIVED names distinct. So this layers on top instead.
 *
 * A selection with nothing named seeds identically to buildDefaultNames.
 */
export function buildSeedNames(
  rows: GenerationRow[],
  groups: Record<string, GroupInfo> = {},
): Record<string, string> {
  const seeds = buildDefaultNames(rows);
  for (const row of rows) {
    const own = (row.name || '').trim();
    if (own) seeds[row.generationId] = own;
    const groupId = row.groupId;
    const groupName = groupId ? (groups[groupId]?.name || '').trim() : '';
    if (groupId && groupName) seeds[groupId] = groupName;
  }
  return seeds;
}

export interface GroupStatus {
  label: string;
  color: 'default' | 'info' | 'warning' | 'success';
  running: number;
  failed: number;
  done: number;
}

/**
 * One status chip for a whole stack. Still-moving members win: a stack that is
 * partly generating reads "Generating 2/5" rather than reporting a failure the
 * user can't act on yet. Only once everything has landed does a failure count
 * take over, and 'Done' means every member finished cleanly.
 */
export function groupStatus(rows: GenerationRow[]): GroupStatus {
  let running = 0;
  let failed = 0;
  let done = 0;
  for (const row of rows) {
    if (row.status === 'PENDING' || row.status === 'RUNNING') running += 1;
    else if (row.status === 'FAILED') failed += 1;
    else if (row.status === 'DONE') done += 1;
  }
  if (running) return { label: `Generating ${done}/${rows.length}`, color: 'info', running, failed, done };
  if (failed) return { label: `${failed} failed`, color: 'warning', running, failed, done };
  return { label: 'Done', color: 'success', running, failed, done };
}

/**
 * Does this stack hold shared launch copy? The value is the same
 * object-or-JSON-string shape a row's adCopy carries, parsed identically to
 * AdCopyPanel.parseAdCopy — asked here so callers can warn before an action
 * throws the copy away.
 */
export function groupHasAdCopy(info: GroupInfo): boolean {
  const raw = info.adCopy;
  const value = typeof raw === 'string'
    ? (() => { try { return JSON.parse(raw); } catch { return null; } })()
    : raw;
  const some = (arr: unknown) => Array.isArray(arr) && arr.some(v => typeof v === 'string' && v.trim());
  return some(value?.headlines) || some(value?.captions);
}

/**
 * How the finalize dialog splits a selection: everything ungrouped in one
 * bucket, plus one section per group that the user can publish as a batch
 * (one name, one Notion row) or as separate creatives.
 */
export type FinalizeSection =
  | { kind: 'loose'; rows: GenerationRow[] }
  | { kind: 'group'; groupId: string; mode: 'batch' | 'individual'; rows: GenerationRow[] };

/**
 * Sections + the names typed into the dialog → the wire items.
 *
 * A 'batch' group collapses to ONE item keyed by groupId (the server numbers
 * the uploads name-1…-N and writes a single Notion row), so its name is looked
 * up under the groupId. Everything else — loose rows, and an 'individual'
 * group's members — is a per-generation item named under its generationId,
 * exactly what this dialog sent before groups existed.
 *
 * `seeds` is what the dialog PREFILLED. It is required, not optional: a
 * default would silently mark every item as renamed, and one publish would
 * then stamp an auto-generated name onto every creative in the batch.
 *
 * Row order inside a batch item does not decide the numbering: the server
 * re-sorts a group's members by creation time before numbering them, which is
 * what keeps `<name>-1…-N` pointing at the same takes across a re-publish.
 */
export function buildFinalizeItems(
  sections: FinalizeSection[],
  names: Record<string, string>,
  seeds: Record<string, string>,
): FinalizeItem[] {
  // `saveName` ONLY when the operator changed the name away from what the
  // dialog seeded. The dialog always sends a name, so persisting
  // unconditionally would stamp every creative with an auto-generated name
  // after one publish and nothing would ever be unnamed again.
  const edited = (key: string, name: string) => (
    name !== (seeds[key] || '').trim() ? { saveName: true } : {}
  );
  const items: FinalizeItem[] = [];
  for (const section of sections) {
    if (section.kind === 'group' && section.mode === 'batch') {
      const name = (names[section.groupId] || '').trim();
      items.push({
        groupId: section.groupId,
        generationIds: section.rows.map(row => row.generationId),
        name,
        ...edited(section.groupId, name),
      });
    } else {
      for (const row of section.rows) {
        const name = (names[row.generationId] || '').trim();
        items.push({ generationId: row.generationId, name, ...edited(row.generationId, name) });
      }
    }
  }
  return items;
}
